import { Check, CircleAlert, Mail } from "lucide-react";
import ApplicationForm from "../components/careers/ApplicationForm";
import { JobFacts } from "../components/careers/JobCard";
import { jobSections } from "../components/careers/job-sections";
import ContentBlocks from "../components/content/ContentBlocks";
import RichText from "../components/content/RichText";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import { ButtonLink } from "../components/ui/Button";
import SectionHeading from "../components/ui/SectionHeading";
import TextLink from "../components/ui/TextLink";
import { pageTitle } from "../content";
import { benefits, careersMailto, openPositionAt } from "../data/careers";
import { images } from "../data/images";
import { routes } from "../data/site";
import { headingId } from "../lib/content-blocks";

const openingsHref = routes.careers + "#openings";

/**
 * Facts for a posting: its open-positions entry, else what the captured page
 * says under its title ("Full-Time | On-Site — Hancock, Michigan").
 */
function postingFacts(page, path) {
  const position = openPositionAt(path);
  const [heading, subhead] = (page.body || [])
    .filter((block) => block.type === "h1")
    .map((block) => block.text);
  // "Type | Location", or a single label such as "Mid-Senior Level".
  const [type, location] = subhead?.includes("|")
    ? subhead.split("|").map((part) => part.trim())
    : [];
  return {
    title: heading || pageTitle(page),
    level: type ? undefined : subhead,
    type,
    location,
    ...position,
  };
}

/** The pay section, set large: the first bold run becomes the headline figure. */
function PayHighlight({ html }) {
  return (
    <div className="mb-12 overflow-hidden rounded-brand bg-slate text-white">
      <div className="blueprint px-6 py-7 sm:px-8">
        <h2 id="compensation" className="tag text-[12.5px] text-blue-soft">
          Compensation
        </h2>
        <RichText
          html={html.replace(
            /<\/strong>\s*,\s*(\w)/,
            (_, letter) => "</strong>" + letter.toUpperCase(),
          )}
          className="mt-2 text-[16.5px] text-white/85 [&_strong]:font-display [&_strong]:mb-1 [&_strong]:block [&_strong]:text-[34px] [&_strong]:leading-tight [&_strong]:font-bold [&_strong]:text-yellow md:[&_strong]:text-[42px]"
        />
      </div>
    </div>
  );
}

/** List items of an HTML fragment's first list (already sanitized HTML). */
const listItems = (html) =>
  [...html.matchAll(/<li>([\s\S]*?)<\/li>/g)].map((match) => match[1]);

/** A section's list as a two-column checklist (the posting's Benefits). */
function Checklist({ html }) {
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {listItems(html).map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 rounded-brand border border-line bg-surface px-4 py-3.5 text-[15.5px] leading-snug text-slate [&_strong]:text-ink"
        >
          <span className="mt-px grid size-5 flex-none place-items-center rounded bg-yellow text-ink">
            <Check size={14} strokeWidth={3} aria-hidden="true" />
          </span>
          <span dangerouslySetInnerHTML={{ __html: item }} />
        </li>
      ))}
    </ul>
  );
}

/** The captured description, one titled section after another. */
function Description({ sections }) {
  return sections.map(({ title, html }, i) => (
    <section key={title || i} className="mt-12 first:mt-0">
      {title && (
        <h2
          id={headingId(title, i)}
          className="font-display mb-5 border-l-4 border-yellow pl-4 text-[26px] leading-tight text-ink uppercase md:text-[30px]"
        >
          {title}
        </h2>
      )}
      {/\bbenefits\b/i.test(title) && listItems(html).length ? (
        <Checklist html={html} />
      ) : (
        <RichText html={html} className="prose-content [&>:last-child]:mb-0!" />
      )}
    </section>
  ));
}

function FactsCard({ facts, isOpen }) {
  const rows = [
    ["Role", facts.title],
    ["Level", facts.level],
    ["Type", facts.type],
    ["Location", facts.location],
    ["Pay", facts.compensation],
  ].filter(([, value]) => value);
  return (
    <div className="rounded-brand bg-ink p-6 text-white shadow-lift">
      <span className="tag text-[11.5px] text-yellow">
        {isOpen ? "At a glance" : "Position closed"}
      </span>
      <dl className="mt-3 mb-6">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex justify-between gap-4 border-t border-white/12 py-3 text-[15px]"
          >
            <dt className="text-white/65">{label}</dt>
            <dd className="text-right font-bold">{value}</dd>
          </div>
        ))}
      </dl>
      {isOpen ? (
        <ButtonLink href="#apply" size="md" className="w-full">
          Apply now
        </ButtonLink>
      ) : (
        <ButtonLink href={openingsHref} size="md" className="w-full">
          See open positions
        </ButtonLink>
      )}
      <a
        href={careersMailto(`Question about the ${facts.title} role`)}
        className="mt-4 flex items-center justify-center gap-2 text-[15px] font-bold text-white/85 transition-colors hover:text-yellow"
      >
        <Mail size={17} aria-hidden="true" className="text-yellow" />
        Questions? Email us
      </a>
    </div>
  );
}

function BenefitsTeaser() {
  return (
    <div className="rounded-brand border border-line bg-surface p-6">
      <span className="tag text-[11.5px] text-blue-deep">Life at Xeratec</span>
      <ul className="mt-3 mb-5 flex flex-col gap-2.5 text-[15px] font-bold text-slate">
        {benefits.map(({ title }) => (
          <li key={title} className="flex items-start gap-2.5">
            <span className="mt-0.5 grid size-5 flex-none place-items-center rounded bg-yellow text-ink">
              <Check size={14} strokeWidth={3} aria-hidden="true" />
            </span>
            {title}
          </li>
        ))}
      </ul>
      <TextLink href={routes.careers + "#benefits"}>
        Benefits, perks &amp; the Keweenaw
      </TextLink>
    </div>
  );
}

/** Closing section of an open posting: the application form. */
function Apply({ job }) {
  return (
    <section id="apply" className="bg-surface px-4 py-20 sm:px-6 md:py-24">
      <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-16">
        <SectionHeading
          eyebrow="Sound like you?"
          title="Apply for this *role.*"
        >
          <p>
            Send us your resume and a few lines about yourself. A cover letter
            is welcome but optional. Every application is read by a person, and
            we'll reply by email.
          </p>
        </SectionHeading>
        <ApplicationForm job={job} />
      </div>
    </section>
  );
}

/** A job posting: open positions come from data/careers.js. */
export default function JobPage({ page, path }) {
  const facts = postingFacts(page, path);
  const position = openPositionAt(path);
  const isOpen = Boolean(position);
  const rich = (page.body || [])
    .filter((block) => block.type === "rich")
    .map((block) => block.html)
    .join("");
  const sections = jobSections(rich);
  const pay = sections.find(({ title }) => /compensation/i.test(title));
  const rest = sections.filter((section) => section !== pay);

  return (
    <>
      <PageHero
        eyebrow={isOpen ? "Now hiring · Careers" : "Careers · Past posting"}
        title={facts.title}
        lede={isOpen ? facts.summary : undefined}
        image={page.image || images.teamMeeting}
        imageAlt={
          page.image
            ? page.imageAlt || ""
            : "Xeratec developers working through a project together"
        }
      >
        <JobFacts position={facts} dark />
        {isOpen ? (
          <div className="mt-7 flex flex-wrap gap-3.5">
            <ButtonLink href="#apply">Apply now ↓</ButtonLink>
            <ButtonLink href={routes.careers} variant="glass">
              Life at Xeratec
            </ButtonLink>
          </div>
        ) : (
          <div
            role="note"
            className="mt-7 flex max-w-[620px] flex-col gap-4 rounded-brand border border-yellow/60 bg-yellow/10 p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="flex items-start gap-3 font-bold text-white">
              <CircleAlert
                size={22}
                aria-hidden="true"
                className="mt-0.5 flex-none text-yellow"
              />
              <span>
                This position is no longer open.
                <span className="block text-[15px] font-normal text-white/80">
                  The posting is kept here for reference.
                </span>
              </span>
            </p>
            <ButtonLink href={openingsHref} size="md">
              See open positions
            </ButtonLink>
          </div>
        )}
      </PageHero>

      <section className="bg-white px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-16">
          <article className="min-w-0">
            {pay && <PayHighlight html={pay.html} />}
            {rest.length ? (
              <Description sections={rest} />
            ) : (
              <ContentBlocks
                blocks={page.body}
                title={facts.title}
                skipImages
              />
            )}
          </article>
          <aside
            aria-label="Position summary"
            className="flex flex-col gap-4 lg:sticky lg:top-28"
          >
            <FactsCard facts={facts} isOpen={isOpen} />
            {position?.companyBenefits !== false && <BenefitsTeaser />}
          </aside>
        </div>
      </section>

      {isOpen ? (
        <Apply job={{ ...facts, path }} />
      ) : (
        <CtaPanel
          eyebrow="Still interested?"
          title="See what's *open now.*"
          text="This role has been filled, but we're always glad to meet accountable people who like solving hard problems."
          href={openingsHref}
          label="View open positions"
        />
      )}
    </>
  );
}
